/**
 * S116 — session-static config cache + in-flight dedup.
 *
 * WHY it exists
 *   The app has no shared cache layer, so session-STATIC config (company
 *   settings, notification preferences, packages, subscription, profile,
 *   payment fee settings) was re-fetched by every page/component that mounted
 *   — DashboardLayout, Sidebar and each settings page all hit /company/settings
 *   independently, and again on every navigation. This mirrors the singleton
 *   idea already used by hooks/useUnreadSummary.js, but at the service layer so
 *   every existing caller benefits with no rewrite.
 *
 * WHAT it does
 *   • cachedGet(key, fetcher): resolves the fetcher ONCE per session and shares
 *     the result with every later caller. Concurrent callers share one in-flight
 *     promise (so DashboardLayout + Sidebar mounting together = ONE request).
 *   • invalidateConfig(key): drops one key (call after its mutation) so config
 *     never goes stale. invalidateConfig() with no arg clears everything (login
 *     / logout / auth loss).
 *
 * WHAT it must NEVER cache
 *   Live data — notifications content, badges, events, budgets, readiness,
 *   unread summary. Those stay fetching fresh.
 */

const _cache = new Map(); // key -> resolved value
const _inflight = new Map(); // key -> Promise

// Return a defensive copy so a consumer mutating its result can never corrupt
// the shared cached object. Config is plain JSON, so a structured/JSON clone
// is safe and preserves the original "fresh object per fetch" semantics.
function _clone(value) {
  if (value == null || typeof value !== 'object') return value;
  try {
    return structuredClone(value);
  } catch (_e) {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch (_e2) {
      return value;
    }
  }
}

export async function cachedGet(key, fetcher) {
  if (_cache.has(key)) return _clone(_cache.get(key));
  if (_inflight.has(key)) return _clone(await _inflight.get(key));
  const p = Promise.resolve()
    .then(fetcher)
    .then((val) => {
      _cache.set(key, val);
      _inflight.delete(key);
      return val;
    })
    .catch((err) => {
      _inflight.delete(key);
      throw err;
    });
  _inflight.set(key, p);
  return _clone(await p);
}

export function invalidateConfig(key) {
  if (key == null) {
    _cache.clear();
    _inflight.clear();
    return;
  }
  _cache.delete(key);
  _inflight.delete(key);
}
